import type { ComponentChildren } from 'preact';
import { useEffect, useState } from 'preact/hooks';
import { useLocation } from 'preact-iso';
import { ErrorBox, Notice, toast } from '../../components/ui';
import { useHumanCheck } from '../../components/Turnstile';
import { usePageMeta } from '../../lib/meta';
import { api, errorMessage } from '../../lib/api';
import { safeNext, useSession } from '../../session';

function destination(next: string | null) {
  if (next?.startsWith('checkout:')) {
    const parts = next.slice('checkout:'.length).split(':');
    const [plan, interval] = parts.length === 2 ? parts : ['pro', parts[0]];
    return `/app/account?upgrade=${plan}:${interval}`;
  }
  return next ?? '/library';
}

/** Centered card used by the focused account screens (sign in, sign up, password reset, email confirmation). */
export function AuthCard({ title, lead, children }: { title: string; lead?: ComponentChildren; children: ComponentChildren }) {
  return (
    <main id="main" tabIndex={-1}>
      <div class="auth-wrap">
        <div class="panel auth-card">
          <img class="auth-logo" src="/img/logo-128.png" width="48" height="48" alt="" />
          <h1 style="font-size:26px">{title}</h1>
          {lead && <p class="muted small">{lead}</p>}
          {children}
        </div>
      </div>
    </main>
  );
}

function AuthForm({ mode }: { mode: 'login' | 'signup' }) {
  const { query, route } = useLocation();
  const { me, refresh } = useSession();
  const next = safeNext(query.next);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const signup = mode === 'signup';
  const human = useHumanCheck(signup ? 'signup' : 'login');

  useEffect(() => { if (me) route(destination(next), true); }, [me]);

  const submit = async (e: Event) => {
    e.preventDefault();
    setError(null);
    if (signup && password.length < 10) { setError('Use at least 10 characters for your password.'); return; }
    if (human.required && !human.token) { setError(human.pendingMessage); return; }
    setBusy(true);
    try {
      const addr = email.trim();
      const res = await api<{ verificationSent?: boolean }>(`/v1/auth/${signup ? 'signup' : 'login'}`, { method: 'POST', body: { email: addr, password, client: 'web', turnstileToken: human.token ?? undefined } });
      const m = await refresh();
      if (!m) throw new Error('Signed in, but your session could not be loaded. Check that cookies are enabled.');
      if (signup) toast(res.verificationSent ? `We sent a confirmation link to ${m.user.email}.` : 'Account created. We couldn’t send the confirmation email — resend it from Account.', 'info');
      route(destination(next), true);
    } catch (err) {
      setError(errorMessage(err));
      human.reset();
      setBusy(false);
    }
  };

  const otherHref = `${signup ? '/login' : '/signup'}${next ? `?next=${encodeURIComponent(next)}` : ''}`;
  const forgotHref = `/forgot${email.trim() ? `?email=${encodeURIComponent(email.trim())}` : ''}`;
  return (
    <AuthCard title={signup ? 'Create your account' : 'Sign in'}
      lead={next?.startsWith('checkout:') ? 'Sign in or create an account to continue to secure checkout.' : signup ? 'Free plan, no card required.' : 'Welcome back to Ayonix Studio.'}>
      {!signup && query.reset === '1' && <div style="margin-bottom:16px" data-testid="reset-notice"><Notice tone="success"><p>Password updated. Sign in with your new password.</p></Notice></div>}
      <form class="stack" onSubmit={submit} noValidate>
        <div class="field">
          <label for="email">Email</label>
          <input id="email" class="input" type="email" autoComplete="email" required value={email} onInput={(e) => setEmail(e.currentTarget.value)} />
        </div>
        <div class="field">
          <div class="row between"><label for="password">Password</label>{!signup && <a class="small" href={forgotHref}>Forgot password?</a>}</div>
          <input id="password" class="input" type="password" autoComplete={signup ? 'new-password' : 'current-password'} required minLength={signup ? 10 : 1}
            aria-describedby={signup ? 'pw-hint' : undefined} value={password} onInput={(e) => setPassword(e.currentTarget.value)} />
          {signup && <span id="pw-hint" class="hint">At least 10 characters.</span>}
        </div>
        {human.box}
        {error && <ErrorBox message={error} />}
        <button class="btn primary block" type="submit" disabled={busy}>{busy ? (signup ? 'Creating account…' : 'Signing in…') : signup ? 'Create account' : 'Sign in'}</button>
      </form>
      {signup && <p class="caption muted" style="margin-top:12px">By creating an account you agree to the <a href="/terms">terms</a> and <a href="/privacy">privacy policy</a>. We’ll email you a link to confirm your address.</p>}
      <p class="small" style="margin:16px 0 0">{signup ? 'Already have an account? ' : 'New to Ayonix Studio? '}<a href={otherHref}>{signup ? 'Sign in' : 'Create an account'}</a></p>
    </AuthCard>
  );
}

export function Login() {
  usePageMeta('Sign in', 'Sign in to Ayonix Studio.');
  return <AuthForm mode="login" />;
}

export function Signup() {
  usePageMeta('Create account', 'Create a free Ayonix Studio account.');
  return <AuthForm mode="signup" />;
}
